import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * Eén register voor niet-opgeslagen werk in het beheer. Een scherm meldt zich met
 * `useUnsavedWork(dirty, scope)`; het register waarschuwt dan:
 * - bij het sluiten of verversen van het tabblad (`beforeunload`), voor elk soort werk;
 * - bij een klik op een interne link (menu, tabbalk, links in een scherm): eerst "Weggaan?".
 *
 * Scope:
 * - `page`: werk dat bij dit scherm hoort en weg is zodra je naar een ander scherm gaat
 *   (de mails, doelgroepen, doorverwijzingen, het huidplan in een dossier).
 * - `admin`: het grote beheer-concept (AdminContext). Dat blijft staan als je binnen het beheer
 *   doorklikt (de opslaanbalk gaat mee), dus het waarschuwt alleen bij het beheer verlaten:
 *   uitloggen of een link buiten het beheer.
 *
 * BrowserRouter, dus geen `useBlocker`: links worden in de capture-fase op document onderschept,
 * vóór React Router ze afhandelt. Na "Weggaan" wordt dezelfde link opnieuw geklikt, zodat de
 * router hem gewoon verwerkt. Niet gedekt: de terugknop van de browser en `navigate()` vanuit code
 * (dat is altijd een bewuste stap na opslaan).
 */

export type UnsavedScope = 'page' | 'admin';
/** Waar je heen gaat: een ander scherm in het beheer, of het beheer uit. */
export type LeaveKind = 'page' | 'admin';
type Ctx = {
  report: (id: string, scope: UnsavedScope | null) => void;
  /** Voert `action` meteen uit, of na bevestiging als er werk verloren gaat. */
  confirmLeave: (kind: LeaveKind, action: () => void) => void;
};
const UnsavedWorkContext = createContext<Ctx | null>(null);

/** Gaat er werk verloren bij deze stap? Naar een ander scherm: alleen `page`-werk. Het beheer uit: alles. */
export const losesWork = (scopes: readonly UnsavedScope[], kind: LeaveKind) => kind === 'admin' ? scopes.length > 0 : scopes.includes('page');

/** De vraag "Weggaan?" zelf komt van de app die het register gebruikt (het beheer heeft zijn eigen stijl). */
export type LeaveDialogProps = {
  open: boolean;
  onStay: () => void;
  onLeave: () => void;
};
type ProviderProps = {
  adminBase: string;
  renderDialog: (props: LeaveDialogProps) => ReactNode;
  children: ReactNode;
};
export function UnsavedWorkProvider({
  adminBase,
  renderDialog,
  children
}: ProviderProps) {
  const [entries, setEntries] = useState<Record<string, UnsavedScope>>({});
  const [pending, setPending] = useState<(() => void) | null>(null);
  const scopes = useMemo(() => Object.values(entries), [entries]);
  const scopesRef = useRef(scopes);
  scopesRef.current = scopes;
  // Na "Weggaan" de link opnieuw klikken zonder weer te vragen.
  const bypass = useRef(false);
  const report = useCallback((id: string, scope: UnsavedScope | null) => {
    setEntries(all => {
      if ((all[id] ?? null) === scope) return all;
      const next = {
        ...all
      };
      if (scope) next[id] = scope;else delete next[id];
      return next;
    });
  }, []);
  const confirmLeave = useCallback((kind: LeaveKind, action: () => void) => {
    if (losesWork(scopesRef.current, kind)) setPending(() => action);else action();
  }, []);

  // Tabblad sluiten of verversen: de browser vraagt het zelf (eigen tekst kan niet meer).
  const anything = scopes.length > 0;
  useEffect(() => {
    if (!anything) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [anything]);

  // Interne links onderscheppen.
  useEffect(() => {
    if (!anything) return;
    const onClick = (e: MouseEvent) => {
      if (bypass.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as Element | null)?.closest?.('a[href]');
      if (!(anchor instanceof HTMLAnchorElement) || anchor.hasAttribute('download') || anchor.target && anchor.target !== '_self') return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      // Alleen een ankertje op dezelfde pagina: er gaat niets verloren.
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      const inside = url.pathname === adminBase || url.pathname.startsWith(`${adminBase}/`);
      const kind: LeaveKind = inside ? 'page' : 'admin';
      if (!losesWork(scopesRef.current, kind)) return;
      e.preventDefault();
      e.stopPropagation();
      setPending(() => () => {
        if (anchor.isConnected) {
          bypass.current = true;
          try {
            anchor.click();
          } finally {
            bypass.current = false;
          }
        } else window.location.assign(url.href);
      });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [anything, adminBase]);
  const value = useMemo(() => ({
    report,
    confirmLeave
  }), [report, confirmLeave]);
  return <UnsavedWorkContext.Provider value={value} data-id="element-5520" data-mp="2sz5mtdciocz">
      {children}
      {renderDialog({
      open: pending !== null,
      onStay: () => setPending(null),
      onLeave: () => {
        const go = pending;
        setPending(null);
        go?.();
      }
    })}
    </UnsavedWorkContext.Provider>;
}

/** Meld niet-opgeslagen werk zolang `dirty` waar is. Zonder register (buiten het beheer): doet niets. */
export function useUnsavedWork(dirty: boolean, scope: UnsavedScope = 'page') {
  const ctx = useContext(UnsavedWorkContext);
  const id = useId();
  const report = ctx?.report;
  useEffect(() => {
    if (!report) return;
    report(id, dirty ? scope : null);
    return () => report(id, null);
  }, [report, id, dirty, scope]);
}

/** Een stap die het beheer verlaat (uitloggen) eerst laten bevestigen als er werk verloren gaat. */
export function useConfirmLeave() {
  const ctx = useContext(UnsavedWorkContext);
  return useCallback((kind: LeaveKind, action: () => void) => ctx ? ctx.confirmLeave(kind, action) : action(), [ctx]);
}